import type { ComponentType } from 'react'
import type { RouteObject } from 'react-router-dom'
import { createPermissionGuardedPage } from '@/shared/components/create-permission-guarded-page'
import { importWithChunkReload } from '@/shared/lib/chunk-load-recovery'
import { HiringRequisitionPermissions } from './permissions'

async function lazyPage(
  importPage: () => Promise<Record<string, ComponentType>>,
  exportName: string,
): Promise<ComponentType> {
  const mod = await importWithChunkReload(importPage)
  const Page = mod[exportName]
  if (!Page) throw new Error(`Hiring requisition page "${exportName}" not found`)
  return Page
}

async function lazyRaiseRoute(
  importPage: () => Promise<Record<string, ComponentType>>,
  exportName: string,
): Promise<{ Component: ComponentType }> {
  const Page = await lazyPage(importPage, exportName)
  return {
    Component: createPermissionGuardedPage(HiringRequisitionPermissions.raise, Page, {
      mode: 'any',
      administratorBypass: true,
    }),
  }
}

async function lazyHireRoute(
  importPage: () => Promise<Record<string, ComponentType>>,
  exportName: string,
): Promise<{ Component: ComponentType }> {
  const Page = await lazyPage(importPage, exportName)
  return {
    Component: createPermissionGuardedPage(
      [HiringRequisitionPermissions.hire, 'manage_job_posts'],
      Page,
      {
        mode: 'any',
        administratorBypass: true,
      },
    ),
  }
}

export const hiringRequisitionRoutes: RouteObject[] = [
  {
    path: 'supervisor/hiring-requisitions',
    lazy: () =>
      lazyRaiseRoute(() => import('./pages/hiring-requisitions-list-page'), 'HiringRequisitionsListPage'),
  },
  {
    path: 'supervisor/hiring-requisitions/new',
    lazy: () =>
      lazyRaiseRoute(() => import('./pages/hiring-requisition-editor-page'), 'HiringRequisitionEditorPage'),
  },
  {
    path: 'supervisor/hiring-requisitions/:id',
    lazy: () =>
      lazyRaiseRoute(() => import('./pages/hiring-requisition-editor-page'), 'HiringRequisitionEditorPage'),
  },
  {
    path: 'hiring-requisitions/:id/decide',
    lazy: async () => {
      const Page = await lazyPage(
        () => import('./pages/hiring-requisition-decide-page'),
        'HiringRequisitionDecidePage',
      )
      return { Component: Page }
    },
  },
  {
    path: 'recruitment/hiring-requisitions',
    lazy: () =>
      lazyHireRoute(
        () => import('./pages/hiring-requisitions-recruiter-page'),
        'HiringRequisitionsRecruiterPage',
      ),
  },
]
